import { 
  Target, Users, DollarSign, Rocket, Briefcase, FileText, CheckCircle, BarChart3,
  Zap, Shield, TrendingUp, Sparkles, Star, Award
} from 'lucide-react';

export interface PrixParTaille {
  '25-30': string;
  '30-50': string;
  '50-75': string;
  '75-125': string;
  '125-250': string;
  '250+': string;
}

export interface ParcoursVente {
  id: string;
  nom: string;
  description: string;
  niveau: 'strategique' | 'tactique' | 'operationnel' | 'mixte';
  icon: any;
  montant: PrixParTaille; // Prix par taille d'entreprise
  duree: string;
  livrables: string[];
  commentVendre: string;
  pourQui: string;
  objections?: Array<{
    objection: string;
    reponse: string;
  }>;
  valeur: string;
}

export const parcoursStrategiques: ParcoursVente[] = [
  {
    id: 's1',
    nom: '🎯 "Le Visionnaire" - Plan Directeur TI Complet',
    description: 'Vision stratégique 3-5 ans avec Plan Directeur TI complet et gouvernance',
    niveau: 'strategique',
    icon: Target,
    montant: {
      '25-30': '$6,000/mois',
      '30-50': '$8,000/mois',
      '50-75': '$10,000/mois',
      '75-125': '$12,000/mois',
      '125-250': '$15,000/mois',
      '250+': '$18,000/mois'
    },
    duree: '2-3 ans',
    livrables: [
      'Plan Directeur TI 3-5 ans',
      'Cadre de gouvernance TI',
      'Alignement stratégique TI / Business',
      'Budget stratégique multi-années',
      'Vision transformation digitale',
      'Matrice RACI gouvernance',
      'Cadre d\'objectifs stratégiques'
    ],
    commentVendre: 'Monsieur le client, avez-vous une vision claire de où vous voulez être dans 3-5 ans ? Ce parcours vous donne exactement ça : une feuille de route stratégique complète. C\'est comme avoir un GPS pour les 5 prochaines années. Sans ça, vous réagissez au lieu d\'anticiper.',
    pourQui: 'Entreprises qui veulent transformer leurs TI en avantage concurrentiel. Idéal pour les PME en croissance ou les entreprises qui n\'ont jamais eu de vision TI structurée.',
    objections: [
      {
        objection: '"C\'est trop cher pour juste un document"',
        reponse: 'Ce n\'est pas "juste un document". C\'est votre FEUILLE DE ROUTE pour les 3-5 prochaines années. Sans ça, vous allez dépenser 3-4 fois plus en projets mal alignés. Le Plan Directeur vous fait ÉCONOMISER de l\'argent à long terme.'
      },
      {
        objection: '"On n\'a pas besoin de ça, on gère bien nos TI"',
        reponse: 'C\'est excellent que vous gérez bien ! Mais gérer, c\'est réactif. Le niveau stratégique, c\'est ANTICIPER. C\'est comme avoir un GPS : vous savez où vous allez avant de partir. Sans stratégie, vous conduisez sans destination.'
      }
    ],
    valeur: 'Le client passe de "réactif" à "proactif". Il sait où il va, pourquoi il y va, et comment y arriver. C\'est ça qui transforme les TI d\'un centre de coûts en avantage concurrentiel.'
  },
  {
    id: 's2',
    nom: '🏛️ "Le Gouverneur" - Gouvernance TI & Comité',
    description: 'Mise en place complète de la gouvernance TI avec Comité TI structuré',
    niveau: 'strategique',
    icon: Users,
    montant: {
      '25-30': '$5,000/mois',
      '30-50': '$6,500/mois',
      '50-75': '$8,000/mois',
      '75-125': '$10,000/mois',
      '125-250': '$12,000/mois',
      '250+': '$15,000/mois'
    },
    duree: '1-2 ans',
    livrables: [
      'Cadre de gouvernance TI',
      'Mise en place Comité TI',
      'Matrice RACI complète',
      'Politiques et procédures TI',
      'Cadre de décision stratégique',
      'Documentation gouvernance',
      'Formation équipe gouvernance'
    ],
    commentVendre: 'Monsieur le client, comment prenez-vous vos décisions TI aujourd\'hui ? Ce parcours structure votre gouvernance TI avec un Comité TI professionnel. C\'est comme avoir un conseil d\'administration pour vos TI. Ça transforme la façon dont vous gérez vos TI.',
    pourQui: 'Entreprises qui prennent des décisions TI de manière informelle ou qui veulent professionnaliser leur gouvernance. Idéal pour les PME qui grandissent.',
    objections: [
      {
        objection: '"On n\'a pas besoin d\'un comité, on décide déjà bien"',
        reponse: 'C\'est excellent ! Mais décider "bien" de manière informelle, c\'est différent de décider de manière STRUCTURÉE et DOCUMENTÉE. Le Comité TI, c\'est la différence entre "on fait au feeling" et "on fait selon un processus professionnel".'
      }
    ],
    valeur: 'Le client passe d\'une gouvernance informelle à une gouvernance structurée et professionnelle. Les décisions sont documentées, alignées avec la stratégie, et prises par les bonnes personnes.'
  },
  {
    id: 's3',
    nom: '💰 "Le Stratège Financier" - Budget & ROI TI',
    description: 'Planification budgétaire stratégique TI avec cadre ROI et KPI financiers',
    niveau: 'strategique',
    icon: DollarSign,
    montant: {
      '25-30': '$4,500/mois',
      '30-50': '$6,000/mois',
      '50-75': '$7,500/mois',
      '75-125': '$9,500/mois',
      '125-250': '$12,000/mois',
      '250+': '$14,000/mois'
    },
    duree: '2-3 ans',
    livrables: [
      'Budget stratégique TI multi-années',
      'Cadre ROI TI',
      'KPI financiers TI',
      'Plan d\'investissement TI',
      'Analyse coûts/bénéfices',
      'Dashboard financier TI',
      'Plan de financement projets'
    ],
    commentVendre: 'Monsieur le client, savez-vous combien vous dépensez vraiment en TI et quel est votre ROI ? Ce parcours vous donne une vision financière complète de vos TI. C\'est comme avoir un CFO pour vos TI.',
    pourQui: 'Entreprises qui veulent optimiser leurs dépenses TI et démontrer le ROI. Idéal pour les entreprises où les TI sont vues comme un centre de coûts.',
    objections: [
      {
        objection: '"On a déjà un budget TI"',
        reponse: 'Excellent ! Mais votre budget est-il aligné avec une stratégie ? Est-ce que chaque dollar dépensé contribue à votre vision ? Ce parcours s\'assure que votre budget sert votre stratégie, pas juste payer les factures.'
      }
    ],
    valeur: 'Le client passe de "on dépense en TI" à "on investit stratégiquement en TI avec ROI mesurable". Les TI deviennent un investissement mesurable, pas juste un coût.'
  },
  {
    id: 's4',
    nom: '🚀 "Le Transformateur" - Vision Transformation Digitale',
    description: 'Vision complète de transformation digitale avec roadmap stratégique',
    niveau: 'strategique',
    icon: Rocket,
    montant: {
      '25-30': '$7,000/mois',
      '30-50': '$9,500/mois',
      '50-75': '$12,000/mois',
      '75-125': '$15,000/mois',
      '125-250': '$18,000/mois',
      '250+': '$22,000/mois'
    },
    duree: '3 ans',
    livrables: [
      'Vision transformation digitale',
      'Plan Directeur transformation',
      'Roadmap transformation 3-5 ans',
      'Analyse maturité digitale',
      'Stratégie innovation',
      'Plan d\'adoption technologies',
      'Cadre changement organisationnel'
    ],
    commentVendre: 'Monsieur le client, où voulez-vous être dans 5 ans ? Ce parcours vous donne une vision complète de votre transformation digitale. C\'est comme avoir une carte complète pour transformer votre entreprise.',
    pourQui: 'Entreprises qui veulent transformer leur modèle d\'affaires avec le digital. Idéal pour les entreprises traditionnelles qui veulent se moderniser.',
    objections: [
      {
        objection: '"On fait déjà du digital"',
        reponse: 'C\'est excellent ! Mais faire du digital, c\'est différent d\'avoir une VISION de transformation digitale. Ce parcours vous donne la stratégie complète pour transformer votre entreprise, pas juste ajouter des outils.'
      }
    ],
    valeur: 'Le client passe de "on fait du digital" à "on a une vision complète de transformation digitale". L\'entreprise devient proactive dans sa transformation au lieu de réagir aux tendances.'
  }
];

export const parcoursTactiques: ParcoursVente[] = [
  {
    id: 't1',
    nom: '🗺️ "Le Cartographe" - Roadmap Annuelle Complète',
    description: 'Roadmap TI 12-24 mois avec suivi trimestriel et ajustements',
    niveau: 'tactique',
    icon: Briefcase,
    montant: {
      '25-30': '$2,500/mois',
      '30-50': '$3,200/mois',
      '50-75': '$4,000/mois',
      '75-125': '$5,000/mois',
      '125-250': '$6,500/mois',
      '250+': '$8,000/mois'
    },
    duree: '1 an',
    livrables: [
      'Roadmap TI 12-24 mois',
      'Plan de projets prioritaires',
      'Budget annuel TI',
      'Révisions trimestrielles',
      'Suivi progression posture TI',
      'Ajustements roadmap',
      'Rapports d\'avancement'
    ],
    commentVendre: 'Monsieur le client, vous avez une vision stratégique ? Parfait ! Mais avez-vous un PLAN concret pour les 12-24 prochains mois ? Ce parcours vous donne votre roadmap annuelle complète avec suivi trimestriel. C\'est votre GPS pour l\'année.',
    pourQui: 'Entreprises qui ont une vision stratégique mais qui manquent de planification tactique. Idéal pour les entreprises qui veulent structurer leurs projets TI.',
    objections: [
      {
        objection: '"On fait déjà des projets"',
        reponse: 'Faire des projets, c\'est bien. Mais sont-ils PRIORISÉS selon votre stratégie ? Ce parcours s\'assure que vous faites les BONS projets dans le BON ordre. Sinon, vous dépensez de l\'argent sur des projets qui ne servent pas votre vision.'
      }
    ],
    valeur: 'Le client passe de "on fait des projets" à "on fait les BONS projets dans le BON ordre". Il maximise son ROI, évite les projets inutiles, et avance vers sa vision stratégique de manière structurée.'
  },
  {
    id: 't2',
    nom: '📋 "L\'Architecte" - ODS Structurée & Budget',
    description: 'Offre de Service (ODS) complète avec budget annuel et cadres KPI',
    niveau: 'tactique',
    icon: FileText,
    montant: {
      '25-30': '$2,800/mois',
      '30-50': '$3,600/mois',
      '50-75': '$4,500/mois',
      '75-125': '$5,800/mois',
      '125-250': '$7,200/mois',
      '250+': '$9,000/mois'
    },
    duree: '1 an',
    livrables: [
      'ODS (Offre de Service) complète',
      'Budget annuel TI',
      'Cadre d\'objectifs et KPI',
      'Plan de livraison services',
      'Suivi performance services',
      'Révisions semestrielles',
      'Rapports de performance'
    ],
    commentVendre: 'Monsieur le client, avez-vous une ODS claire avec votre fournisseur TI ? Ce parcours structure votre offre de service avec budget, KPI, et suivi. C\'est comme avoir un contrat professionnel pour vos services TI.',
    pourQui: 'Entreprises qui veulent structurer leur relation avec leur fournisseur TI. Idéal pour les entreprises qui ont des services TI mais pas de structure claire.',
    objections: [
      {
        objection: '"On a déjà un contrat avec notre fournisseur"',
        reponse: 'Excellent ! Mais votre contrat couvre-t-il les livrables, les KPI, les révisions ? Ce parcours structure votre relation avec des livrables clairs, des KPI mesurables, et des révisions régulières.'
      }
    ],
    valeur: 'Le client passe d\'une relation informelle à une relation structurée avec livrables clairs, KPI mesurables, et suivi régulier. La valeur est mesurable et documentée.'
  },
  {
    id: 't3',
    nom: '🎯 "Le Planificateur" - Projets Prioritaires & Jalons',
    description: 'Planification détaillée des projets prioritaires avec jalons et suivi',
    niveau: 'tactique',
    icon: CheckCircle,
    montant: {
      '25-30': '$2,200/mois',
      '30-50': '$2,800/mois',
      '50-75': '$3,500/mois',
      '75-125': '$4,500/mois',
      '125-250': '$5,800/mois',
      '250+': '$7,200/mois'
    },
    duree: '1 an',
    livrables: [
      'Plan de projets prioritaires',
      'Jalons et échéances',
      'Budget par projet',
      'Suivi avancement projets',
      'Rapports de statut',
      'Gestion risques projets',
      'Révisions mensuelles'
    ],
    commentVendre: 'Monsieur le client, comment priorisez-vous vos projets TI ? Ce parcours structure votre planification de projets avec jalons, budgets, et suivi. C\'est comme avoir un chef de projet pour tous vos projets TI.',
    pourQui: 'Entreprises qui ont plusieurs projets TI mais qui manquent de structure de planification. Idéal pour les entreprises qui veulent améliorer leur gestion de projets.',
    objections: [
      {
        objection: '"On gère déjà nos projets"',
        reponse: 'C\'est excellent ! Mais gérez-vous vos projets avec jalons clairs, budgets définis, et suivi régulier ? Ce parcours structure votre gestion de projets pour maximiser le succès.'
      }
    ],
    valeur: 'Le client passe d\'une gestion de projets informelle à une gestion structurée avec jalons, budgets, et suivi. Les projets sont mieux planifiés et plus réussis.'
  },
  {
    id: 't4',
    nom: '📊 "Le Mesureur" - KPI & Performance TI',
    description: 'Cadre complet de KPI TI avec dashboard et suivi performance',
    niveau: 'tactique',
    icon: BarChart3,
    montant: {
      '25-30': '$1,800/mois',
      '30-50': '$2,400/mois',
      '50-75': '$3,000/mois',
      '75-125': '$3,800/mois',
      '125-250': '$4,800/mois',
      '250+': '$6,000/mois'
    },
    duree: '1 an',
    livrables: [
      'Cadre d\'objectifs et KPI TI',
      'Dashboard performance TI',
      'Rapports de performance',
      'Suivi tendances',
      'Analyse performance',
      'Recommandations amélioration',
      'Révisions mensuelles'
    ],
    commentVendre: 'Monsieur le client, comment mesurez-vous la performance de vos TI ? Ce parcours vous donne un cadre complet de KPI avec dashboard et suivi. C\'est comme avoir un tableau de bord pour vos TI.',
    pourQui: 'Entreprises qui veulent mesurer et améliorer la performance de leurs TI. Idéal pour les entreprises qui veulent des données pour prendre des décisions.',
    objections: [
      {
        objection: '"On sait déjà si ça fonctionne"',
        reponse: 'C\'est excellent ! Mais "savoir" de manière subjective, c\'est différent de MESURER avec des données objectives. Ce parcours vous donne des KPI mesurables pour prendre des décisions basées sur des données.'
      }
    ],
    valeur: 'Le client passe de "on pense que ça fonctionne" à "on MESURE que ça fonctionne avec des données". Les décisions sont basées sur des données, pas sur des impressions.'
  }
];

export const parcoursOperationnels: ParcoursVente[] = [
  {
    id: 'o1',
    nom: '⚡ "Le Réactif" - Support TI Réactif',
    description: 'Support TI réactif avec gestion incidents et changements',
    niveau: 'operationnel',
    icon: Zap,
    montant: {
      '25-30': '$1,200/mois',
      '30-50': '$1,500/mois',
      '50-75': '$1,800/mois',
      '75-125': '$2,200/mois',
      '125-250': '$2,800/mois',
      '250+': '$3,500/mois'
    },
    duree: '1 an',
    livrables: [
      'Support TI réactif',
      'Gestion incidents',
      'Gestion changements',
      'Monitoring basique',
      'Rapports incidents',
      'Documentation basique',
      'Réponse aux demandes'
    ],
    commentVendre: 'Monsieur le client, avez-vous un support TI réactif ? Ce parcours vous donne un support TI professionnel avec gestion d\'incidents et changements. C\'est la base pour que vos TI fonctionnent bien.',
    pourQui: 'Entreprises qui n\'ont pas de support TI structuré ou qui veulent améliorer leur support actuel. Idéal pour les petites entreprises.',
    objections: [
      {
        objection: '"On gère déjà nos TI nous-mêmes"',
        reponse: 'C\'est excellent ! Mais gérez-vous vos TI de manière STRUCTURÉE avec processus ITIL, documentation, et suivi ? Ce parcours structure votre support TI pour être plus efficace.'
      }
    ],
    valeur: 'Le client passe d\'un support informel à un support structuré avec processus ITIL. Les incidents sont résolus plus rapidement et de manière plus professionnelle.'
  },
  {
    id: 'o2',
    nom: '🛡️ "Le Protecteur" - Sécurité Opérationnelle',
    description: 'Sécurité TI opérationnelle avec monitoring et réponse incidents',
    niveau: 'operationnel',
    icon: Shield,
    montant: {
      '25-30': '$1,500/mois',
      '30-50': '$1,900/mois',
      '50-75': '$2,400/mois',
      '75-125': '$3,000/mois',
      '125-250': '$3,800/mois',
      '250+': '$4,800/mois'
    },
    duree: '1 an',
    livrables: [
      'Monitoring sécurité',
      'Détection menaces',
      'Réponse incidents sécurité',
      'Politiques sécurité',
      'Formation sécurité',
      'Rapports sécurité',
      'Conformité basique'
    ],
    commentVendre: 'Monsieur le client, êtes-vous protégé contre les cybermenaces ? Ce parcours vous donne une sécurité TI opérationnelle avec monitoring et réponse. C\'est votre première ligne de défense.',
    pourQui: 'Entreprises qui veulent améliorer leur sécurité TI opérationnelle. Idéal pour les entreprises qui n\'ont pas de sécurité structurée.',
    objections: [
      {
        objection: '"On n\'a jamais eu de problème"',
        reponse: 'C\'est excellent ! Mais "jamais eu de problème" jusqu\'à présent, ce n\'est pas la même chose que "protégé contre les menaces". Ce parcours vous protège PROACTIVEMENT au lieu d\'attendre qu\'un problème arrive.'
      }
    ],
    valeur: 'Le client passe d\'une sécurité réactive à une sécurité proactive avec monitoring et détection. Les menaces sont détectées et traitées avant qu\'elles ne deviennent des problèmes.'
  },
  {
    id: 'o3',
    nom: '📈 "Le Surveillant" - Monitoring & Performance',
    description: 'Monitoring complet TI avec outils et dashboards opérationnels',
    niveau: 'operationnel',
    icon: TrendingUp,
    montant: {
      '25-30': '$1,400/mois',
      '30-50': '$1,700/mois',
      '50-75': '$2,100/mois',
      '75-125': '$2,600/mois',
      '125-250': '$3,300/mois',
      '250+': '$4,200/mois'
    },
    duree: '1 an',
    livrables: [
      'Monitoring infrastructure',
      'Dashboards opérationnels',
      'Alertes automatiques',
      'Rapports performance',
      'Outils monitoring',
      'Formation équipe',
      'Maintenance outils'
    ],
    commentVendre: 'Monsieur le client, savez-vous en temps réel ce qui se passe avec vos TI ? Ce parcours vous donne un monitoring complet avec dashboards et alertes. C\'est comme avoir un tableau de bord pour vos TI.',
    pourQui: 'Entreprises qui veulent améliorer leur visibilité sur leurs TI. Idéal pour les entreprises qui veulent détecter les problèmes avant qu\'ils n\'affectent les utilisateurs.',
    objections: [
      {
        objection: '"On sait déjà si ça fonctionne"',
        reponse: 'C\'est excellent ! Mais "savoir" après qu\'un problème arrive, c\'est différent de DÉTECTER les problèmes avant qu\'ils n\'affectent les utilisateurs. Ce parcours vous donne une visibilité proactive.'
      }
    ],
    valeur: 'Le client passe d\'une détection réactive à une détection proactive. Les problèmes sont détectés et résolus avant qu\'ils n\'affectent les utilisateurs.'
  },
  {
    id: 'o4',
    nom: '🔧 "L\'Optimiseur" - Optimisation Opérationnelle',
    description: 'Optimisation des processus opérationnels TI avec amélioration continue',
    niveau: 'operationnel',
    icon: Sparkles,
    montant: {
      '25-30': '$1,600/mois',
      '30-50': '$2,000/mois',
      '50-75': '$2,500/mois',
      '75-125': '$3,200/mois',
      '125-250': '$4,000/mois',
      '250+': '$5,000/mois'
    },
    duree: '1 an',
    livrables: [
      'Analyse processus opérationnels',
      'Optimisation processus',
      'Amélioration continue',
      'Documentation processus',
      'Formation équipe',
      'Suivi améliorations',
      'Rapports optimisation'
    ],
    commentVendre: 'Monsieur le client, vos processus TI sont-ils optimisés ? Ce parcours optimise vos processus opérationnels TI pour être plus efficaces. C\'est comme avoir un consultant en optimisation pour vos TI.',
    pourQui: 'Entreprises qui veulent améliorer l\'efficacité de leurs processus TI. Idéal pour les entreprises qui ont des processus mais qui veulent les optimiser.',
    objections: [
      {
        objection: '"Nos processus fonctionnent déjà"',
        reponse: 'C\'est excellent ! Mais "fonctionnent" ne veut pas dire "optimisés". Ce parcours identifie les opportunités d\'amélioration pour rendre vos processus plus efficaces et moins coûteux.'
      }
    ],
    valeur: 'Le client passe de processus "qui fonctionnent" à processus "optimisés". Les processus sont plus efficaces, moins coûteux, et plus satisfaisants pour les utilisateurs.'
  }
];

export const parcoursMixtes: ParcoursVente[] = [
  {
    id: 'm1',
    nom: '🌟 "Le Complet" - Solution d\'Affaire Holistique',
    description: 'Parcours complet couvrant les 3 niveaux : stratégique, tactique et opérationnel',
    niveau: 'mixte',
    icon: Star,
    montant: {
      '25-30': '$5,000/mois',
      '30-50': '$6,500/mois',
      '50-75': '$8,000/mois',
      '75-125': '$10,000/mois',
      '125-250': '$12,500/mois',
      '250+': '$15,000/mois'
    },
    duree: '2-3 ans',
    livrables: [
      'Plan Directeur TI 3-5 ans',
      'Roadmap 12-24 mois',
      'ODS complète',
      'Gouvernance TI',
      'Support TI opérationnel',
      'Monitoring complet',
      'KPI et performance',
      'Révisions trimestrielles'
    ],
    commentVendre: 'Monsieur le client, voulez-vous une solution COMPLÈTE pour vos TI ? Ce parcours couvre TOUT : la vision stratégique, le plan tactique, ET l\'opérationnel. C\'est comme avoir une maison TI complète : fondations, murs, et toit.',
    pourQui: 'Entreprises qui veulent une solution complète et structurée pour leurs TI. Idéal pour les entreprises qui veulent tout faire en une fois.',
    objections: [
      {
        objection: '"C\'est trop complet, on n\'a pas besoin de tout ça"',
        reponse: 'Je comprends ! Mais regardez : vous avez besoin de la vision (stratégique), du plan (tactique), ET du support (opérationnel). Sans l\'un ou l\'autre, c\'est incomplet. Ce parcours vous donne TOUT ce dont vous avez besoin.'
      }
    ],
    valeur: 'Le client a une solution COMPLÈTE pour ses TI : vision, plan, ET exécution. C\'est la différence entre avoir des TI qui "fonctionnent" et avoir des TI qui "transforment l\'entreprise".'
  },
  {
    id: 'm2',
    nom: '🎯 "Le Stratège Opérationnel" - Stratégie + Opérations',
    description: 'Combinaison stratégique et opérationnelle pour entreprises en croissance',
    niveau: 'mixte',
    icon: Target,
    montant: {
      '25-30': '$4,000/mois',
      '30-50': '$5,200/mois',
      '50-75': '$6,500/mois',
      '75-125': '$8,200/mois',
      '125-250': '$10,500/mois',
      '250+': '$13,000/mois'
    },
    duree: '2 ans',
    livrables: [
      'Plan Directeur TI',
      'Vision stratégique',
      'Support TI opérationnel',
      'Monitoring complet',
      'Gouvernance basique',
      'KPI opérationnels',
      'Révisions semestrielles'
    ],
    commentVendre: 'Monsieur le client, vous avez besoin de la vision stratégique ET du support opérationnel ? Ce parcours combine les deux : vous avez la vision (où aller) ET le support (pour y arriver).',
    pourQui: 'Entreprises en croissance qui ont besoin de vision stratégique mais aussi de support opérationnel solide. Idéal pour les entreprises qui grandissent rapidement.',
    objections: [
      {
        objection: '"On veut juste le support"',
        reponse: 'Je comprends ! Mais le support sans vision stratégique, c\'est comme conduire sans destination. Ce parcours vous donne les deux : le support pour aujourd\'hui ET la vision pour demain.'
      }
    ],
    valeur: 'Le client a à la fois la vision stratégique et le support opérationnel. Il peut avancer vers sa vision tout en ayant un support solide au quotidien.'
  },
  {
    id: 'm3',
    nom: '📋 "Le Planificateur Complet" - Tactique + Opérations',
    description: 'Planification tactique complète avec support opérationnel',
    niveau: 'mixte',
    icon: Briefcase,
    montant: {
      '25-30': '$3,200/mois',
      '30-50': '$4,000/mois',
      '50-75': '$5,000/mois',
      '75-125': '$6,300/mois',
      '125-250': '$8,000/mois',
      '250+': '$10,000/mois'
    },
    duree: '1-2 ans',
    livrables: [
      'Roadmap 12-24 mois',
      'ODS structurée',
      'Budget annuel',
      'Support TI opérationnel',
      'Gestion incidents',
      'Monitoring',
      'KPI tactiques et opérationnels',
      'Révisions trimestrielles'
    ],
    commentVendre: 'Monsieur le client, vous avez besoin d\'un plan tactique ET d\'un support opérationnel ? Ce parcours vous donne les deux : le plan pour l\'année ET le support pour l\'exécuter.',
    pourQui: 'Entreprises qui ont besoin de planification tactique structurée avec support opérationnel. Idéal pour les entreprises qui veulent structurer leurs projets TI.',
    objections: [
      {
        objection: '"On veut juste le plan"',
        reponse: 'Je comprends ! Mais un plan sans support pour l\'exécuter, c\'est comme avoir une recette sans cuisinier. Ce parcours vous donne le plan ET le support pour l\'exécuter.'
      }
    ],
    valeur: 'Le client a un plan tactique structuré avec le support opérationnel pour l\'exécuter. Il peut planifier ET exécuter efficacement.'
  },
  {
    id: 'm4',
    nom: '🚀 "Le Transformateur Complet" - Transformation + Support',
    description: 'Transformation digitale complète avec support opérationnel',
    niveau: 'mixte',
    icon: Rocket,
    montant: {
      '25-30': '$5,500/mois',
      '30-50': '$7,200/mois',
      '50-75': '$9,000/mois',
      '75-125': '$11,500/mois',
      '125-250': '$14,500/mois',
      '250+': '$18,000/mois'
    },
    duree: '3 ans',
    livrables: [
      'Vision transformation digitale',
      'Plan Directeur transformation',
      'Roadmap transformation',
      'Support TI opérationnel',
      'Gestion changement',
      'Formation équipe',
      'Monitoring transformation',
      'Révisions trimestrielles'
    ],
    commentVendre: 'Monsieur le client, vous voulez transformer votre entreprise ? Ce parcours vous donne la transformation complète AVEC le support pour la réaliser. C\'est comme avoir un architecte ET un constructeur.',
    pourQui: 'Entreprises qui veulent transformer leur modèle d\'affaires avec le digital ET qui ont besoin de support opérationnel. Idéal pour les entreprises traditionnelles qui se modernisent.',
    objections: [
      {
        objection: '"On veut juste la transformation"',
        reponse: 'Je comprends ! Mais la transformation sans support opérationnel, c\'est comme construire une maison sans fondations. Ce parcours vous donne la transformation ET le support pour la réaliser.'
      }
    ],
    valeur: 'Le client a une vision de transformation complète avec le support opérationnel pour la réaliser. Il peut transformer son entreprise de manière structurée et soutenue.'
  },
  {
    id: 'm5',
    nom: '🛡️ "Le Sécurisé Complet" - Cybersécurité + Opérations',
    description: 'Cybersécurité stratégique et opérationnelle complète',
    niveau: 'mixte',
    icon: Shield,
    montant: {
      '25-30': '$4,500/mois',
      '30-50': '$5,800/mois',
      '50-75': '$7,200/mois',
      '75-125': '$9,000/mois',
      '125-250': '$11,500/mois',
      '250+': '$14,000/mois'
    },
    duree: '2 ans',
    livrables: [
      'Plan cybersécurité stratégique',
      'Audit cybersécurité NIST',
      'Politiques sécurité',
      'Monitoring sécurité',
      'Réponse incidents sécurité',
      'Formation sécurité',
      'Conformité sécurité',
      'Révisions trimestrielles'
    ],
    commentVendre: 'Monsieur le client, la cybersécurité est-elle votre priorité ? Ce parcours vous donne une cybersécurité COMPLÈTE : stratégie ET opérations. C\'est comme avoir une armée ET une défense.',
    pourQui: 'Entreprises qui veulent une cybersécurité complète et structurée. Idéal pour les entreprises qui traitent des données sensibles ou qui sont dans des secteurs réglementés.',
    objections: [
      {
        objection: '"On a déjà de la sécurité"',
        reponse: 'C\'est excellent ! Mais avez-vous une STRATÉGIE de cybersécurité avec monitoring, politiques, et formation ? Ce parcours structure votre cybersécurité de manière complète.'
      }
    ],
    valeur: 'Le client a une cybersécurité complète et structurée : stratégie ET opérations. Il est protégé de manière proactive et réactive.'
  },
  {
    id: 'm6',
    nom: '💼 "Le Professionnel" - Gouvernance + Tactique + Opérations',
    description: 'Gouvernance professionnelle avec planification tactique et support opérationnel',
    niveau: 'mixte',
    icon: Award,
    montant: {
      '25-30': '$4,800/mois',
      '30-50': '$6,200/mois',
      '50-75': '$7,800/mois',
      '75-125': '$9,800/mois',
      '125-250': '$12,500/mois',
      '250+': '$15,500/mois'
    },
    duree: '2 ans',
    livrables: [
      'Gouvernance TI professionnelle',
      'Comité TI structuré',
      'Roadmap 12-24 mois',
      'ODS structurée',
      'Support TI opérationnel',
      'Monitoring complet',
      'KPI gouvernance et opérations',
      'Révisions trimestrielles'
    ],
    commentVendre: 'Monsieur le client, voulez-vous une gouvernance TI PROFESSIONNELLE ? Ce parcours vous donne gouvernance, planification, ET support. C\'est comme avoir un conseil d\'administration, un plan d\'affaires, ET une équipe opérationnelle.',
    pourQui: 'Entreprises qui veulent professionnaliser complètement leurs TI. Idéal pour les entreprises qui veulent être au niveau des grandes entreprises.',
    objections: [
      {
        objection: '"C\'est trop pour nous"',
        reponse: 'Je comprends ! Mais regardez : les grandes entreprises ont gouvernance, planification, ET support. Pourquoi pas vous ? Ce parcours vous donne le même niveau de professionnalisme.'
      }
    ],
    valeur: 'Le client a une gouvernance TI professionnelle avec planification tactique et support opérationnel. Ses TI sont au niveau des grandes entreprises.'
  }
];

export const tousLesParcours: ParcoursVente[] = [
  ...parcoursStrategiques,
  ...parcoursTactiques,
  ...parcoursOperationnels,
  ...parcoursMixtes
];

